import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

// The member screens need real site chrome (nav, banner, footer) but also React
// state, which the injected-HTML pages can't give us. So we inject the same
// template shell and portal the React tree into the empty slot inside it.

const THEME_CSS = [
  '/theme/css/bootstrap.css',
  '/theme/css/style.css',
  '/theme/css/fonts.css',
  '/brand/overrides.css',
];

function ensureThemeCss(): void {
  for (const href of THEME_CSS) {
    if (!document.querySelector(`link[data-theme-css="${href}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.dataset.themeCss = href;
      document.head.appendChild(link);
    }
  }
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(s);
  });
}

const isInternalRoute = (href: string): boolean =>
  href.startsWith('/') && !/\.[a-z0-9]+$/i.test(href);

export function TemplateFrame({ title, children }: { title: string; children: React.ReactNode }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    ensureThemeCss();
    document.documentElement.classList.add('wide');

    (async () => {
      const html = await fetch('/theme/pages/member.html').then((r) => r.text());
      if (cancelled || !containerRef.current) return;
      containerRef.current.innerHTML = html;

      // Same parallax fallback the content pages use: without it the banner's
      // white text can land on white.
      containerRef.current.querySelectorAll<HTMLElement>('[data-parallax-img]').forEach((el) => {
        const img = el.dataset.parallaxImg;
        if (!img) return;
        el.style.backgroundImage = `url("${img}")`;
        el.style.backgroundSize = 'cover';
        el.style.backgroundPosition = 'center';
      });

      const w = window as unknown as { jQuery?: unknown };
      if (!w.jQuery) await loadScript('/theme/js/core.min.js');
      if (cancelled) return;
      await loadScript(`/theme/js/script.js?_=${Date.now()}`);
      if (cancelled || !containerRef.current) return;

      containerRef.current.querySelectorAll('a[href]').forEach((el) => {
        const a = el as HTMLAnchorElement;
        const href = a.getAttribute('href') ?? '';
        if (/^https?:\/\//i.test(href)) {
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          return;
        }
        if (href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('#')) return;
        if (!isInternalRoute(href)) return;
        a.addEventListener('click', (e) => {
          e.preventDefault();
          navigate(href);
        });
      });

      setSlot(containerRef.current.querySelector<HTMLElement>('#member-slot'));
    })().catch((err) => {
      console.error('[member] failed to render the shell', err);
    });

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  // The banner title belongs to the shell, so it's set once the shell is in.
  useEffect(() => {
    const el = containerRef.current?.querySelector('#member-title');
    if (el) el.textContent = title;
  }, [title, slot]);

  return (
    <>
      <div ref={containerRef} />
      {slot ? createPortal(children, slot) : null}
    </>
  );
}
